/**
 * Draft › Teams — built to turn `9a`, from the canvas.
 *
 * "Ten rosters, five across, no scrolling." The drawing's own summary of what the spreadsheet
 * got right, and the three things it could not do:
 *
 *   1. A card lists only the picks that GM will ACTUALLY MAKE. An acquired pick appears in the
 *      roster that owns it — italic `from Rajan` while it is still unmade — and a traded-away
 *      round simply is not there, because it belongs to someone else now. This is the whole
 *      reason the view exists: on a spreadsheet, a traded pick is in two places or neither.
 *   2. Keepers are marked rather than typed in by hand. The drawing marks them by tinting the
 *      row in the GM's own hue and setting the pick code bold, not with a `K` chip — the turn's
 *      prose says `K`, the drawing tints, and the drawing wins.
 *   3. The slot tally goes amber the moment a STARTING slot is unfilled — not at zero. A lone
 *      receiver is amber (two start) and a lone tight end is not (one starts). Same
 *      `belowStarters()` rule the 8a rail's counts grid uses, so the two can never disagree.
 *
 * EVERY CARD IS THE SAME HEIGHT BY CONSTRUCTION — 15px rows, a fixed 255px list, and identical
 * header, tally and bye strips. That is a real requirement, not a detail: a card that grows as
 * picks land makes the whole grid jump every time somebody drafts, which on draft night is the
 * difference between a reference and a distraction. 255px is seventeen rows, which is sixteen
 * rounds plus the one extra a GM can hold after a pick trade.
 *
 * The bye strip is drawn as a wireframe "pending real bye data". It is not pending — the player
 * index has carried a bye per player all along — so it is built as drawn with real weeks and the
 * `wireframe` tag dropped, the same call made for the 8a rail's chart.
 */

import { useMemo, useState } from 'react';
import { managerShort } from '../domain/identity.js';
import { pickCode, type BoardPick } from '../domain/board.js';
import { adpByName, adpKey, belowStarters } from '../domain/draftTools.js';
import { hueFor } from './GmBadge.js';
import { BYE_WEEKS, NflChip, nflInfoOf } from './NflChip.js';
import { shortSurname, splitName } from './BoardGrid.js';

/** The five slots the tally reports, in the drawing's order. */
const SLOTS = ['QB', 'RB', 'WR', 'TE', 'DEF'] as const;

/** Seventeen 15px rows. Sixteen rounds, plus the one extra a pick trade can leave a GM. */
const LIST_ROWS = 17;

export type TeamSort = 'order' | 'made' | 'name' | 'pos';

/**
 * BY POSITION, WITHIN EVERY CARD AT ONCE (Tim, 8 September: "add a Position sort that sorts all
 * rosters by QB, RB, WR, TE, DEF and within the positions, orders the players by ADP").
 *
 * The other three sorts reorder the CARDS — which manager is where in the grid. This one
 * reorders the ROWS inside all ten of them, which is a different question: not "whose card do I
 * want first" but "what has each of these teams actually got". Ten rosters in draft order are
 * ten shuffled hands; in position order they are ten teams you can compare down the column.
 *
 * The picks not yet made keep their own order and follow, because a round nobody has used is
 * not a position — and the card has to stay exactly seventeen rows tall either way.
 */
const POS_ORDER = new Map(SLOTS.map((pos, i) => [pos, i]));

interface Props {
  board: readonly BoardPick[];
  order: readonly string[];
  clock: BoardPick | null;
  teams: number;
  /** The manager looking at the screen — his card is named in the accent. */
  me?: string;
}

export function TeamCards({ board, order, clock, teams, me }: Props) {
  const [sort, setSort] = useState<TeamSort>('order');
  /*
   * Where the consensus takes a man. Unranked sorts last inside his position rather than first:
   * a rookie nobody has heard of is not the best receiver on the roster.
   */
  const adp = useMemo(() => {
    const m = adpByName();
    return (player: string) => m.get(adpKey(player)) ?? Infinity;
  }, []);

  const cards = useMemo(() => {
    const built = order.map((gm, seat) => {
      /*
       * By OWNER, not by seat. A pick Vishal bought from Rajan is Vishal's to make and belongs
       * on Vishal's card; Rajan's card should not show a row he cannot use.
       */
      const inOrder = board
        .filter((p) => p.owner.toUpperCase() === gm.toUpperCase())
        .sort((a, b) => a.overall - b.overall);
      const made = inOrder.filter((p) => p.player);
      const rank = (p: BoardPick) => POS_ORDER.get((p.pos ?? '').toUpperCase() as never) ?? SLOTS.length;
      const mine = sort !== 'pos' ? inOrder : [
        ...made.slice().sort((a, b) => rank(a) - rank(b)
          || adp(a.player ?? '') - adp(b.player ?? '')
          || (a.player ?? '').localeCompare(b.player ?? '')),
        ...inOrder.filter((p) => !p.player),
      ];

      /*
       * A SECTION SAYS WHAT IT IS (Tim, 9 September: "when sorting by position in team view,
       * label a line above each position section with QB/RB/WR/TE/DEF").
       *
       * Sorted by position, a card is five runs of names with nothing between them — and the row
       * itself carries no position, so reading which run you are in means recognising the players.
       * That is fine on your own roster and no use at all on the other nine, which is the whole
       * reason to sort this way. One label per run, only where a run actually starts, so a card
       * with no tight end never draws an empty TE heading.
       *
       * The unmade tail below is deliberately unlabelled: those rows are rounds, not a position,
       * and they already read as a different thing — grey, dotted, no name on them.
       */
      const rows: Array<{ head: string } | { pick: BoardPick }> = sort !== 'pos'
        ? mine.map((p) => ({ pick: p }))
        : (() => {
          const out: Array<{ head: string } | { pick: BoardPick }> = [];
          let last: string | null = null;
          for (const p of mine) {
            const pos = p.player ? (p.pos ?? '').toUpperCase() : '';
            if (pos && pos !== last) { out.push({ head: pos }); last = pos; }
            out.push({ pick: p });
          }
          return out;
        })();

      const have = (pos: string) => made.filter((p) => (p.pos ?? '') === pos).length;
      const tally = SLOTS.map((pos) => ({ pos, n: have(pos), short: belowStarters(pos, have(pos)) }));

      const byes = new Map<number, string[]>();
      for (const p of made) {
        const wk = nflInfoOf(p.player ?? '')?.bye ?? p.bye ?? null;
        if (wk === null) continue;
        byes.set(wk, [...(byes.get(wk) ?? []), p.player ?? '']);
      }

      return { gm, seat, picks: mine, rows, made: made.length, tally, byes };
    });

    if (sort === 'name') return [...built].sort((a, b) => managerShort(a.gm).localeCompare(managerShort(b.gm)));
    if (sort === 'made') return [...built].sort((a, b) => b.made - a.made || a.seat - b.seat);
    /* Position sorts the rows, not the cards: the grid stays in draft order. */
    return built;
  }, [board, order, sort, adp]);

  const picksIn = board.filter((p) => p.player).length;

  return (
    <div className={`teamsview${sort === 'pos' ? ' bypos' : ''}`}>
      <div className="teamsbar">
        <div className="mut">
          {picksIn} of {board.length} picks in
          {clock && <> · <b>{pickCode(clock, teams)}</b> on the clock</>}
        </div>
        <label className="teamsort">
          <span className="mut">sort</span>
          <select value={sort} onChange={(e) => setSort(e.target.value as TeamSort)}>
            <option value="order">Draft order</option>
            <option value="made">Picks made</option>
            <option value="name">Manager</option>
            <option value="pos">Position</option>
          </select>
        </label>
      </div>

      <div className="teamcards">
        {cards.map((c) => {
          const h = hueFor(c.gm);
          const isMe = Boolean(me && c.gm.toUpperCase() === me.toUpperCase());
          return (
            <section className={`tcard ${isMe ? 'me' : ''}`} key={c.gm}>
              <header style={{ background: `hsl(${h} 62% 94%)` }}
                title={`${managerShort(c.gm)} — ${c.made} picked`}>
                <b style={{ color: `hsl(${h} 45% 26%)` }}>{managerShort(c.gm)}</b>
                <span className="seat">seat {c.seat + 1}</span>
              </header>

              <div className="tclist">
                {c.rows.map((r) => ('head' in r
                  ? <div className="tcsect" key={`h${r.head}`}>{r.head}</div>
                  : <Row key={r.pick.overall} p={r.pick} teams={teams} hue={h} />))}
                {/* The list is a fixed height, so the empty tail is real space, not a gap. */}
                {c.rows.length < LIST_ROWS
                  && Array.from({ length: LIST_ROWS - c.rows.length }, (_, i) => (
                    <div className="tcrow pad" key={`pad${i}`} aria-hidden="true" />
                  ))}
              </div>

              {/* 17d: one foot row — the tally on the left, the bye bars on the right. */}
              <div className="tcfoot">
              <div className="tctally">
                {c.tally.map((t) => (
                  <span className={t.short ? 'short' : ''} key={t.pos}
                    title={t.short ? `${t.pos}: below the starting lineup` : `${t.pos}: ${t.n}`}>
                    {t.pos} <b>{t.n}</b>
                  </span>
                ))}
              </div>

              <div className="tcbyes" title="Bye weeks — amber once two of the roster share one">
                <div className="tcbars">
                  {BYE_WEEKS.map((wk) => {
                    const who = c.byes.get(wk) ?? [];
                    // 8px of bar per player, the drawing's rate; amber once two of a roster
                    // share a week, which is the number a GM actually acts on. Capped so a
                    // fourth player cannot push the bar out of a fixed-height card.
                    const px = who.length === 0 ? 2 : Math.min(22, who.length * 8);
                    return (
                      <div className="tcbar" key={wk}
                        title={who.length ? `Week ${wk}: ${who.join(', ')}` : `Week ${wk}: nobody`}>
                        <i style={{
                          height: `${px}px`,
                          background: who.length >= 2 ? 'var(--warn)'
                            : who.length === 1 ? '#cbd3e0' : '#f2f4f7',
                        }} />
                        <span>{wk}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              </div>
            </section>
          );
        })}
      </div>

      <p className="note">
        A card shows only the picks that manager will actually make: an acquired pick sits in the
        roster that owns it and a traded-away round is not listed at all. A tally goes amber when
        a <b>starting</b> slot is unfilled, not at zero. The bye bars go amber from two stacked.
      </p>
    </div>
  );
}

function Row({ p, teams, hue }: { p: BoardPick; teams: number; hue: number }) {
  const code = pickCode(p, teams);
  const acquired = p.owner.toUpperCase() !== p.seat.toUpperCase();

  if (!p.player) {
    /*
     * An unmade pick is the code and a hairline. An unmade pick he BOUGHT says where it came
     * from instead — that is the row a GM double-checks before he trades again, and italic is
     * the drawing's way of saying "yours, but not yet".
     */
    return (
      <div className={`tcrow open ${acquired ? 'acq' : ''}`}>
        <span className="code">{code}</span>
        {acquired
          ? <em>from {managerShort(p.seat)}</em>
          : <i className="rule" aria-hidden="true" />}
      </div>
    );
  }

  const { last, first } = splitName(p.player);
  const info = nflInfoOf(p.player);
  const bye = info?.bye ?? p.bye ?? null;
  /*
   * The row is about 62px of name. The given name earns its place — this league has both
   * Ja'Marr Chase and Chase Brown, which is exactly the collision it resolves — but only where
   * the surname leaves room for it. Past that the surname alone is the honest thing to show,
   * and the full name is on the row's tooltip regardless.
   */
  const surname = shortSurname(last);
  const room = surname.length <= 7;

  const where = [p.pos, info?.team, bye != null ? `bye ${bye}` : null].filter(Boolean).join(' · ');
  return (
    <div className={`tcrow ${p.keeper ? 'kept' : ''}`}
      style={p.keeper ? { background: `hsl(${hue} 62% 96%)` } : undefined}
      title={`${p.player} — ${p.keeper ? `kept, charged ${code}` : code}${where ? ` · ${where}` : ''}`}>
      <span className="code">{code}</span>
      <span className="pn">
        <b>{surname}</b>
        {first && room && <i>{first}</i>}
      </span>
      {p.keeper && <span className={`tcpill ${p.farm ? 'farm' : ''}`}>{p.farm ? 'Farm' : 'Keeper'}</span>}
      {/* The chip and the week stay for the old layout; Pass 17 hides them and reads the tooltip. */}
      {info?.team ? <NflChip team={info.team} /> : <span className="nfl none" />}
      <span className="bye">{bye ?? ''}</span>
    </div>
  );
}
